import { CollegamentoIstanzaDettaglioDTODirezione } from '@framework/api-client/api/backoffice/schemas/collegamentoIstanzaDettaglioDTODirezione.ts';
import { CollegamentoIstanzaDettaglioDTOTipo } from '@framework/api-client/api/backoffice/schemas/collegamentoIstanzaDettaglioDTOTipo.ts';
import {
  useGetFrontAreaPubblicaRichiesteId,
  useGetFrontAreaPubblicaRichiesteIdCartelle,
} from '@framework/api-client/api/inquiries-compile/area-pubblica-osservazioni-controller/area-pubblica-osservazioni-controller.ts';
import { type CollegamentoRichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/collegamentoRichiestaDTO.ts';
import { useGetFrontTipiDomandaId } from '@framework/api-client/api/inquiries-compile/tipi-domanda/tipi-domanda.ts';
import { type ApplicantsTableCell } from '@framework/common-web/schema/submissions/applicants/ApplicantsTableCell.tsx';
import { selectTranslatedInquiryType } from '@framework/common-web/schema/translations/selectTranslatedInquiryType.ts';
import { TitleSubmissionRow } from '@framework/common-web/ui/inquiry/submission/TitleSubmissionRow.tsx';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import { type BrandedPath } from '@framework/ui/core/router/TypedRoute.tsx';
import { useIdFromUrl } from '@framework/ui/hooks/useIdFromUrl.ts';
import { Button, Flex, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps } from 'react-router';
import styled, { useTheme } from 'styled-components';
import { useCreateSubmissionAndLinks } from '../../../components/inquiries/hooks/useCreateSubmissionAndLinks.ts';
import { PageLayout } from '../../../ui/layout/PageLayout.tsx';
import { NetworkAlertPage } from '../../errors/network-alert/NetworkAlertPage.tsx';
import { LoadingPage } from '../../loading/LoadingPage.tsx';
import { FolderTreeTable } from './components/folder-table/FolderTreeTable.tsx';
import { ProcedureDataView } from './components/ProcedureDataView.tsx';
import { SubmissionDataView } from './components/SubmissionDataView.tsx';

const TitleDescriptionSpan = styled.span`
  font-weight: 400;
`;

interface Params {
  id: string;
}

export interface IObservationPageProps extends RouteComponentProps<Params> {}

/**
 * Pagina di dettaglio di un'osservazione (richiesta pubblica)
 *
 * //TODO Controllare se è corretto e tutti i ## sono da eliminare
 */
export function ObservationPage(props: IObservationPageProps) {
  const [submissionId] = useIdFromUrl(props, 'id');
  const { t } = useTranslation();
  const theme = useTheme();

  const { data, error, isLoading, isFetching, isError } =
    useGetFrontAreaPubblicaRichiesteId(submissionId!, {
      query: { keepPreviousData: true },
    });
  const submission = data?.richiesta;
  const observation = data;

  const applicants = SubmissionLogic.getApplicant(
    submission,
  ) as ApplicantsTableCell;

  const rawInquiry = useGetFrontTipiDomandaId(submission?.idTipoDomanda ?? '', {
    query: { enabled: true, select: selectTranslatedInquiryType },
  });
  const inquiry = rawInquiry.data;

  const foldersResult = useGetFrontAreaPubblicaRichiesteIdCartelle(
    submissionId!,
    {
      query: { keepPreviousData: true },
    },
  );

  const links: CollegamentoRichiestaDTO[] = submission
    ? [
        {
          idRichiesta: submission.id!,
          tipo: CollegamentoIstanzaDettaglioDTOTipo.OSSERVAZIONE,
          direzione: CollegamentoIstanzaDettaglioDTODirezione.USCITA,
        },
      ]
    : [];

  const { handleClick } = useCreateSubmissionAndLinks(
    inquiry?.tipoDomandaOsservante?.id,
    links,
  );

  useBreadcrumbItem({ path: '/area-pubblica', title: 'Area Pubblica' });
  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: data?.richiesta?.progressivo,
  });

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isError || foldersResult.isError) {
    return (
      <NetworkAlertPage
        //TODO Controllare Errori NetworkAlertPage
        error={error ?? foldersResult.error}
        message={t(
          'observation.errore-durante-il-caricamento',
          'Si è verificato un errore durante il caricamento.',
        )}
      />
    );
  }

  return (
    <PageLayout>
      <ResponsiveCard
        hideCardHeader={theme.searchButtonOnTableFlag}
        title={
          <TitleSubmissionRow
            right={
              inquiry?.tipoDomandaOsservante &&
              observation?.osservabile && (
                <Button size="large" onClick={handleClick}>
                  {t('observation.nuova-osservazione', 'Nuova Osservazione')}
                </Button>
              )
            }
            stateTag={
              <Space size="large">
                <TitleDescriptionSpan>
                  {t(
                    'observation.numero-procedimento-suffix',
                    'Numero Procedimento:',
                  )}{' '}
                  <b>{observation?.numeroProcedimento ?? 'N/A'}</b>
                </TitleDescriptionSpan>
                {submission?.progressivo !==
                  observation?.numeroProcedimento && (
                  <TitleDescriptionSpan>
                    {t('observation.numero-istanza-suffix', 'Numero Istanza:')}{' '}
                    <b>{submission?.progressivo ?? 'N/A'}</b>
                  </TitleDescriptionSpan>
                )}
              </Space>
            }
          >
            {observation?.oggettoProcedimento ??
              submission?.oggetto ??
              `${
                submission?.titoloTipoDomanda ??
                t('observation.domanda', 'Domanda')
              } N.${submission?.progressivo}`}
          </TitleSubmissionRow>
        }
      >
        <Flex vertical gap={24}>
          <ProcedureDataView
            submission={submission}
            osservation={observation}
          />

          <SubmissionDataView submission={submission} />

          <FolderTreeTable
            data={foldersResult.data}
            loading={foldersResult.isLoading}
          />
        </Flex>
      </ResponsiveCard>
    </PageLayout>
  );
}
